import axios from "axios";
import api from "@/services/api";

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";

export const PLANIFICATIONS_BASE_URL =
  process.env.VUE_APP_PLANIFICATIONS_URL || "https://pruebas.utopie.cl/api";

// Per-scope ticket cache. Slide decks and image generation can fan out into
// dozens of calls; refetching a ticket each time would hammer main for no
// benefit since the same JWT is replayable until it expires. Scopes that
// charge the uses ledger ("structured") are never cached — each one must
// debit afresh.
const ticketCache = new Map(); // endpoint -> { ticket, expMs }
const TICKET_REFRESH_MARGIN_MS = 30 * 1000;
const NON_CACHEABLE_SCOPES = new Set([
  "structured",
  "page_questions",
  "report",
]);

export function clearPlanificationTicketCache() {
  ticketCache.clear();
}

// Ask the main server (where the user session + uses ledger live) for a
// short-lived JWT that authorizes one or more planification requests against
// the worker server. Cached scopes return the same ticket until it nears
// expiry; charging scopes always fetch a fresh one.
export function fetchPlanificationTicket(endpoint) {
  const cacheable = !NON_CACHEABLE_SCOPES.has(endpoint);
  if (cacheable) {
    const cached = ticketCache.get(endpoint);
    if (cached && cached.expMs - Date.now() > TICKET_REFRESH_MARGIN_MS) {
      return Promise.resolve(cached.ticket);
    }
  }
  return api
    .post("issue_planification_ticket/", { endpoint })
    .then((response) => {
      const { ticket, exp } = response.data;
      if (cacheable && exp) {
        ticketCache.set(endpoint, { ticket, expMs: exp * 1000 });
      }
      return ticket;
    });
}

// Ambitos que usa la generacion del mazo de diapositivas.
const SCOPES_SLIDES = ["slide", "slide_image"];

/**
 * Renueva los tickets del mazo ANTES de empezar a generarlo.
 *
 * El mazo tarda del orden de dos minutos. Si la sesion en el servidor principal
 * se vence a mitad de camino, cada diapositiva que falta pide su ticket, recibe
 * 401 y falla sin avisar: el profesor ya gasto su uso y se queda mirando una
 * seccion que nunca avanza (visto en produccion el 2026-08-20, doce 401
 * seguidos).
 *
 * Pidiendo tickets frescos justo antes de arrancar se consiguen dos cosas: se
 * empieza con la ventana completa por delante, y si la sesion ya estaba caida
 * se sabe AL PRINCIPIO —cuando todavia se puede avisar— en vez de a la mitad.
 *
 * Rechaza si el servidor principal no los emite; el llamador decide que mostrar.
 */
export function renovarTicketsSlides() {
  SCOPES_SLIDES.forEach((scope) => ticketCache.delete(scope));
  return Promise.all(SCOPES_SLIDES.map((scope) => fetchPlanificationTicket(scope)));
}

/** true si el error viene de una sesion caida y no de un fallo de generacion. */
export function esSesionCaida(error) {
  const status =
    (error && error.response && error.response.status) ||
    (error && error.status);
  return status === 401 || status === 403;
}

const bearerConfig = (ticket) => ({
  baseURL: PLANIFICATIONS_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${ticket}`,
  },
});

export function planificationFetchHeaders(ticket) {
  return {
    Accept: "application/json",
    Authorization: `Bearer ${ticket}`,
  };
}

// Returns an axios instance scoped to a single ticket. Create one per call;
// tickets are short-lived and (in the strict version) single-use.
export function planificationApiFor(ticket) {
  return axios.create({ ...bearerConfig(ticket), timeout: 50000000 });
}

export function planificationBlobFor(ticket) {
  return axios.create({
    ...bearerConfig(ticket),
    timeout: 60000,
    responseType: "blob",
  });
}
